import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Stack,
  Divider,
  Checkbox,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import zIndex from '@mui/material/styles/zIndex';

// Assets
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as BinIcon } from "assets/images/icons/icn_btn_bin.svg";
import { ReactComponent as CheckBoxDefault } from "assets/images/icons/checkbox_default.svg";
import { ReactComponent as CheckBoxFilled } from "assets/images/icons/checkbox_fiilled.svg";
import { ReactComponent as CheckBoxClear } from "assets/images/icons/checkbox_clear.svg";
import { ReactComponent as AddIcon } from "assets/images/icons/btn_add.svg";
import { ReactComponent as UploadIcon } from "assets/images/icons/icn_btn_export_pressed.svg";

// Components
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import UploadView from "./UploadView";

const MainView = ({
  viewStateEnum,
  viewState,
  getViewTitle,
  form,
  formValue,
  handleMainView,
  handleUploadView,
  changeViewState,
  closeModal,
  selectedIndexes,
  setSelectedIndexes,
}) => {
  const theme = useTheme();
  const inputRefs = useRef([]);
  const viewRef = useRef(null);

  // Status for mask calculation
  const [viewWidth, setViewWidth] = useState(0);
  const [viewHeight, setViewHeight] = useState(0);

  useEffect(() => {
    if (viewRef.current) {
      setViewWidth(viewRef.current.offsetWidth);
      setViewHeight(viewRef.current.offsetHeight);
    }
  }, [viewState]);

  let marginTop = 0;
  if (viewHeight > 0) {
    marginTop = viewHeight / 15;
  }

  const isFormValid = () => {
    if (formValue.licenseKeys.length === 0) return false;
    // Check if ALL license keys are fully filled (20 chars)
    return formValue.licenseKeys.every(
      item => item.keySegments.join("").length === 20
    );
  };

  const handleKeyChange = (rowIndex, segIndex, value) => {
    const uppercaseValue = value.toUpperCase().slice(0, 5);
    handleMainView.updateKeySegment(rowIndex, segIndex, uppercaseValue);

    // Auto focus next input
    if (uppercaseValue.length === 5 && segIndex < 3) {
      const nextIndex = rowIndex * 4 + segIndex + 1;
      if (inputRefs.current[nextIndex]) {
        inputRefs.current[nextIndex].focus();
      }
    }
  };

  const handleKeyDown = (e, rowIndex, segIndex) => {
    // Backspace to previous input
    if (e.key === "Backspace" && e.target.value === "" && segIndex > 0) {
      const prevIndex = rowIndex * 4 + segIndex - 1;
      if (inputRefs.current[prevIndex]) {
        inputRefs.current[prevIndex].focus();
      }
    }
  };

  const handlePaste = (e, rowIndex, segIndex) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text");
    // 只保留英數字並轉換為大寫
    const cleanKey = pasteData.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

    if (cleanKey.length > 0) {
      handleMainView.pasteLicenseKey(rowIndex, segIndex, cleanKey);

      // 計算貼上後應自動聚焦的輸入框索引
      const pastedSegmentsCount = Math.ceil(cleanKey.length / 5);
      const lastPastedSegIndex = Math.min(segIndex + pastedSegmentsCount - 1, 3);
      const focusIndex = rowIndex * 4 + lastPastedSegIndex;

      if (inputRefs.current[focusIndex]) {
        setTimeout(() => {
          inputRefs.current[focusIndex].focus();
        }, 0);
      }
    }
  };

  const handleToggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIndexes(formValue.licenseKeys.map(item => item.id));
    } else {
      setSelectedIndexes([]);
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedIndexes(prev => {
      if (prev.includes(id)) {
        return prev.filter(i => i !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  return (
    <>
      <Box
        ref={viewRef}
        style={{
          position: "relative",
          padding: '0',
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
              paddingLeft: "20px",
            }}
          >
            {getViewTitle(viewStateEnum.MAIN)}
          </Typography>
          <Button
            onClick={closeModal}
            sx={{
              backgroundColor: theme.palette.primary.transparent,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.transparent,
              },
            }}
          >
            <CloseIcon />
          </Button>
        </Box>

        <Box sx={{ marginTop: '10px', paddingLeft: "26px", }}>
          <Box sx={form.getRowStyle()}>
            <Box sx={form.getLabelStyle()}>License Key List</Box>
            <Box display="flex" gap="10px" justifyContent={"flex-end"} sx={{ flexGrow: 1 }}>
              <OutlineRectangleButton
                startIcon={<AddIcon />}
                height="32px"
                onClick={handleMainView.addLicenseKey}
              >
                Add
              </OutlineRectangleButton>
              {/* Feature will reopen */}
              {/* <OutlineRectangleButton
                startIcon={<UploadIcon />}
                height="32px"
                onClick={() => changeViewState(viewStateEnum.UPLOAD)}
              >
                Upload
              </OutlineRectangleButton> */}
              {selectedIndexes.length > 0 && (
                <OutlineRectangleButton
                  startIcon={<BinIcon />}
                  height="32px"
                  onClick={() => handleMainView.deleteSelectedIds(selectedIndexes)}
                >
                  Delete<Box sx={form.getNumberStyle()}>{selectedIndexes.length}</Box>
                </OutlineRectangleButton>
              )}
            </Box>
          </Box>

          <Box sx={{ ...form.getTableStyle() }}>
            <TableContainer
              component={Paper}
              sx={{
                "&.MuiPaper-root": {
                  boxShadow: "none",
                  border: 'none',
                },
              }}
              style={{ height: '300px', overflowY: 'auto' }}
            >
              <Table
                sx={{
                  borderCollapse: "collapse",
                  "& td, & th": {
                    border: "none",
                  },
                  "& tbody tr:nth-of-type(odd)": {
                    backgroundColor: 'rgb(246, 246, 246)',
                  },
                  "& tbody tr:nth-of-type(even)": {
                    backgroundColor: theme.palette.primary.white,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '40px', padding: '8px 16px' }}>
                      <Checkbox
                        icon={<CheckBoxDefault style={{ width: "24px", height: "24px" }} />}
                        checkedIcon={<CheckBoxFilled style={{ width: "24px", height: "24px" }} />}
                        indeterminateIcon={<CheckBoxClear style={{ width: "24px", height: "24px" }} />}
                        checked={selectedIndexes.length === formValue.licenseKeys.length && formValue.licenseKeys.length > 0}
                        indeterminate={selectedIndexes.length > 0 && selectedIndexes.length < formValue.licenseKeys.length}
                        onChange={(e) => handleToggleSelectAll(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>License Key</TableCell>
                    <TableCell sx={{ width: '40px' }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formValue.licenseKeys.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        sx={{
                          textAlign: "center",
                          padding: "20px",
                        }}
                      >
                        No Entry in the table
                      </TableCell>
                    </TableRow>
                  ) : (
                    formValue.licenseKeys.map((item, rowIndex) => (
                      <TableRow key={item.id}>
                        <TableCell sx={{ width: '40px', padding: '8px 16px' }}>
                          <Checkbox
                            icon={<CheckBoxDefault style={{ width: "24px", height: "24px" }} />}
                            checkedIcon={<CheckBoxFilled style={{ width: "24px", height: "24px" }} />}
                            checked={selectedIndexes.includes(item.id)}
                            onChange={() => handleToggleSelect(item.id)}
                          />
                        </TableCell>
                        <TableCell sx={{ padding: '8px 0' }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {item.keySegments.map((segment, segIndex) => (
                              <React.Fragment key={segIndex}>
                                <TextField
                                  inputRef={(el) => (inputRefs.current[rowIndex * 4 + segIndex] = el)}
                                  sx={{
                                    ...form.getTextfieldStyle(),
                                    width: '70px',
                                    '& .MuiOutlinedInput-root': {
                                      height: '32px',
                                    },
                                    '& input': {
                                      textAlign: 'center',
                                      padding: '4px 8px',
                                      textTransform: 'uppercase'
                                    }
                                  }}
                                  value={segment}
                                  inputProps={{ maxLength: 5 }}
                                  onChange={(e) => handleKeyChange(rowIndex, segIndex, e.target.value)}
                                  onKeyDown={(e) => handleKeyDown(e, rowIndex, segIndex)}
                                  onPaste={(e) => handlePaste(e, rowIndex, segIndex)}
                                />
                                {segIndex < item.keySegments.length - 1 && <Box sx={{ color: theme.palette.primary.greyPrimary }}>—</Box>}
                              </React.Fragment>
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ width: '40px', padding: '8px 16px', textAlign: 'right' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleMainView.removeLicenseKey(item.id)}
                            sx={{
                              color: theme.palette.primary.greyPrimary,
                              '&:hover': { color: theme.palette.primary.redPrimary }
                            }}
                          >
                            <BinIcon style={{ width: '18px' }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={form.getBannerStyle()}>
              <Typography variant="body2">
                Credit amounts are revealed only after you click <strong>Apply</strong> — the server validates each key and returns its value at that point.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            margin: "24px 0 16px auto",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <OutlineButton
            onClick={closeModal}
            sx={{ marginRight: "20px" }}
            width="100px"
            height="32px"
          >
            Cancel
          </OutlineButton>
          <SolidButton
            onClick={handleMainView.apply}
            width="100px"
            height="32px"
            disabled={!isFormValid()}
          >
            Apply
          </SolidButton>
        </Box>

        {
          viewState !== viewStateEnum.MAIN && (
            <Box
              style={{
                ...form.getMaskStyle(),
                width: viewWidth + 40 + 'px',
                height: viewHeight + 60 + 'px',
                transform: `translateY(-20px) translateX(-20px)`,
              }}
            />
          )
        }
      </Box>

      {
        viewState === viewStateEnum.UPLOAD && (
          <Box style={{
            ...form.getInnerViewStyle(zIndex),
            width: viewWidth > 0 ? (viewWidth - 40 + 'px') : '100%',
            transform: `translateY(${marginTop}px) translateX(40px)`,
          }} >
            <Box style={form.getInnerContainerStyle()}>
              <UploadView
                viewStateEnum={viewStateEnum}
                getViewTitle={getViewTitle}
                form={form}
                handleUploadView={handleUploadView}
              />
            </Box>
          </Box>
        )
      }
    </>
  );
};

export default MainView;
